<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Info } from "lucide-vue-next";
  import { Card } from "@/components/ui/card";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useCAdsetsStore } from "@stores/cAdsets";
  import { useCCampaignsStore } from "@stores/cCampaigns";

  const { currentMCampaign } = storeToRefs(useMCampaignStore());
  const { cAdsets } = storeToRefs(useCAdsetsStore());
  const { cCampaigns } = storeToRefs(useCCampaignsStore());

  const sumActiveAdsets = computed(() => {
    const activeCCampaignIds = cCampaigns.value.filter((c) => c.status === "active").map((c) => c.id);
    return cAdsets.value
      .filter((c) => c.status === "active" && activeCCampaignIds.includes(c.c_campaign_id))
      .reduce((sum, c) => sum + Number(c.daily_budget || 0), 0);
  });

  const hasOverride = computed(() => !!currentMCampaign.value?.daily_budget_manual);

  const displayedTotal = computed(() =>
    hasOverride.value ? currentMCampaign.value.daily_budget_manual : sumActiveAdsets.value,
  );

  const subtext = computed(() =>
    hasOverride.value ? `Override active. Sum: €${sumActiveAdsets.value}` : "Sum of all active ad sets",
  );

  const clearOverride = () => {
    currentMCampaign.value.daily_budget_manual = null;
  };
</script>

<template>
  <Card class="p-5">
    <div class="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      Daily budget
      <Tooltip :content="subtext">
        <Info class="h-3.5 w-3.5 cursor-help" />
      </Tooltip>
    </div>
    <div class="mt-2 text-3xl font-semibold tabular-nums">€{{ displayedTotal }}</div>
    <div class="mt-4 flex items-center gap-2">
      <InputNumber
        v-model="currentMCampaign.daily_budget_manual"
        :min="0"
        placeholder="Override daily budget"
        class="h-9" />
      <Button
        v-if="hasOverride"
        variant="ghost"
        size="sm"
        @click="clearOverride">
        Clear
      </Button>
    </div>
  </Card>
</template>
